import type { LegacyFairListNumericRouteParams } from '~/utils/fairSemanticListing'

/** 形如 `/exhibition-16-0-9999-0-0-0-1/` 或 `/seo/exhibition-…`（可选末尾 `/`） */
export const LEGACY_FAIR_LIST_NUMERIC_PATH =
  /^\/(?:seo\/)?exhibition-(\d+)-(\d+)-(\d+)-(\d+)-(\d+)-(\d+)-(\d+)\/?$/i

const LEGACY_FAIR_LIST_LIKE_PATH = /^\/(?:seo\/)?exhibition-\d+(?:-\d+)*\/?$/i

export function normalizeFairListPathname(pathname: string): string {
  return (pathname || '').split('?')[0].replace(/\/+/g, '/') || '/'
}

/**
 * 旧链 `t` / query `m` 月份归一。
 * - 未选月 / 空 / 0 → `0`
 * - `1`–`12`（含 `08`）→ 对应月份
 * - 6 位 YYYYMM（如 `202010` = 2020 年 10 月）→ `NaN`：语义列表只有滚动月 1–12，
 *   不得削成「本年 10 月」再 301；调用方须直接 404
 * - 其余无法得到合法 1–12 → `NaN`（直接 404，禁止先 301）
 */
export function normalizeFairListMonthParam(raw: unknown): number {
  if (raw == null || raw === '') return 0
  const s = String(Array.isArray(raw) ? raw[0] : raw).trim()
  if (!s || s === '0') return 0

  // 绝对年月：与「本年/次年 m 月」不是同一语义，禁止归一后 301
  if (/^\d{6}$/.test(s)) return Number.NaN

  let monthStr = s
  if (/^\d{2}$/.test(s) && s.startsWith('0')) {
    monthStr = s.slice(1)
  }

  const n = Math.floor(Number(monthStr))
  if (!Number.isFinite(n) || n <= 0) return 0
  if (n > 12) return Number.NaN
  return n
}

/** 中文站旧列表 URL（需 301），不含 `/exhibition/ticket/`、`:id.html` 等子路径 */
export function isLegacyFairListPathNeeding301(pathname: string): boolean {
  const path = normalizeFairListPathname(pathname)
  if (path === '/exhibition' || path === '/exhibition/') return true
  if (path === '/seo/exhibition' || path === '/seo/exhibition/') return true
  return LEGACY_FAIR_LIST_NUMERIC_PATH.test(path)
}

/** 明显是 exhibition- 数字旧链，但不符合七段标准格式；服务端应直接 410。 */
export function isMalformedLegacyFairListPath(pathname: string): boolean {
  const path = normalizeFairListPathname(pathname)
  return LEGACY_FAIR_LIST_LIKE_PATH.test(path) && !isLegacyFairListPathNeeding301(path)
}

export function parseLegacyFairListNumericPath(pathname: string): LegacyFairListNumericRouteParams | null {
  const path = normalizeFairListPathname(pathname)
  if (path === '/exhibition' || path === '/exhibition/' || path === '/seo/exhibition' || path === '/seo/exhibition/') {
    return { f: 0, s: 0, g: 9999, p: 0, c: 0, t: 0, page: 1 } // 全球默认七段
  }
  const m = LEGACY_FAIR_LIST_NUMERIC_PATH.exec(path)
  if (!m) return null
  // exhibition-{f}-{s}-{g}-{p}-{c}-{t}-{page}
  const [, f, s, g, p, c, t, page] = m
  return { f, s, g, p, c, t, page }
}
